import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetCasinoRoundsQuery } from '../services/casino/casinoApi';

const CasinoDetailsPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { game, filterDate } = location.state || {};

  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toISOString().slice(0, 10);
  };

  const dateStr = toYMD(filterDate);
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error } = useGetCasinoRoundsQuery(
    { game: game || 'teen20', page, limit, fromDate: dateStr, toDate: dateStr },
    { skip: !game && !dateStr }
  );

  const rounds     = data?.data || [];
  const summary    = data?.summary || {};
  const pagination = data?.pagination || {};
  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.total || 0;
  const overallPL  = Number(summary.overallPL ?? 0);

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
  };

  return (
    <div className="w-full">
      <div className="w-full px-2">
        <div className="bg-white border border-gray-200 rounded shadow">

          {/* Header */}
          <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">{game || 'casino'}</span>
            <button
              onClick={() => navigate(-1)}
              className="bg-white text-gray-800 px-5 py-1 hover:bg-gray-100 transition-colors"
            >
              Back
            </button>
          </div>

          {/* Total P/L */}
          {!isLoading && !error && (
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="text-sm text-gray-500 mb-1">Total</div>
              <div className={`text-3xl font-bold ${overallPL < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {overallPL.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-10"><Loading /></div>
            ) : error ? (
              <div className="py-8 text-center text-red-500 text-sm">Error loading data</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr style={{ background: '#3e525f' }} className="text-white">
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">#</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">EVENT ID</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">OPEN DATE</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">WINNER</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">PL</th>
                    <th className="px-4 py-3 text-left font-medium">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {rounds.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-10 text-center border-t border-gray-200">
                        <div className="flex flex-col items-center gap-2">
                          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                              <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7" />
                              <g fillRule="nonzero" fill="#d9d9d9">
                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" />
                              </g>
                            </g>
                          </svg>
                          <span className="text-gray-400">No data</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rounds.map((r, i) => {
                      const pl = Number(r.pl ?? 0);
                      return (
                        <tr key={r.roundId || i} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-500 border-r border-gray-200">{(page - 1) * limit + i + 1}</td>
                          <td className="px-4 py-3 font-mono text-gray-800 border-r border-gray-200">{r.roundId || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 border-r border-gray-200 whitespace-nowrap">{fmtDate(r.openDate)}</td>
                          <td className="px-4 py-3 text-gray-700 border-r border-gray-200">{r.winner || '—'}</td>
                          <td className={`px-4 py-3 font-semibold border-r border-gray-200 ${pl < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {pl.toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/casino/all-bets/${r.roundId}`)}
                              className="text-white px-4 py-1.5 text-xs hover:opacity-90 transition-opacity"
                              style={{ background: '#3e525f' }}
                            >
                              Show Bets
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {(page - 1) * limit + 1}–{Math.min(page * limit, totalItems)} of {totalItems}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-white" style={{ background: '#3e525f' }}>{page}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CasinoDetailsPage;
